import type { ILegalRepository } from "@/core/repositories/interfaces/ILegalRepository";
import type {
  LegalDocument,
  LegalDocumentSlug,
  UpsertLegalDocumentInput,
} from "@/core/services/types/legal.types";
import { LEGAL_DOCUMENT_SLUGS } from "@/core/services/types/legal.types";
import { createClient } from "@/lib/supabase";

const SELECT_COLUMNS =
  "id, slug, title, content_md, version, locale, is_published, effective_at, created_at, updated_at";

function isLegalSlug(value: string): value is LegalDocumentSlug {
  return (LEGAL_DOCUMENT_SLUGS as readonly string[]).includes(value);
}

function mapRow(row: {
  id: string;
  slug: string;
  title: string;
  content_md: string;
  version: string;
  locale: string;
  is_published: boolean;
  effective_at: string;
  created_at: string;
  updated_at: string;
}): LegalDocument | null {
  if (!isLegalSlug(row.slug)) return null;
  return {
    id: row.id,
    slug: row.slug,
    title: row.title,
    content_md: row.content_md,
    version: row.version,
    locale: row.locale,
    is_published: row.is_published,
    effective_at: row.effective_at,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

export class LegalRepository implements ILegalRepository {
  async findAll(): Promise<LegalDocument[]> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("legal_documents")
      .select(SELECT_COLUMNS)
      .order("slug", { ascending: true })
      .order("locale", { ascending: true })
      .order("version", { ascending: false });

    if (error) {
      console.error("[LegalRepository.findAll]", error.message);
      return [];
    }

    return (data ?? [])
      .map(mapRow)
      .filter((row): row is LegalDocument => row !== null);
  }

  async upsert(input: UpsertLegalDocumentInput): Promise<LegalDocument | null> {
    const supabase = await createClient();
    const locale = (input.locale ?? "fr").trim() || "fr";
    const version = (input.version ?? "1.0").trim() || "1.0";
    const isPublished = input.is_published === true;
    const effectiveAt = input.effective_at?.trim() || new Date().toISOString();

    if (isPublished) {
      let unpublish = supabase
        .from("legal_documents")
        .update({ is_published: false })
        .eq("slug", input.slug)
        .eq("locale", locale);
      if (input.document_id) {
        unpublish = unpublish.neq("id", input.document_id);
      }
      const { error: unpubError } = await unpublish;
      if (unpubError) {
        console.error("[LegalRepository.upsert] unpublish", unpubError.message);
        return null;
      }
    }

    const row = {
      slug: input.slug,
      title: input.title,
      content_md: input.content_md,
      version,
      locale,
      is_published: isPublished,
      effective_at: effectiveAt,
    };

    if (input.document_id) {
      const { data, error } = await supabase
        .from("legal_documents")
        .update(row)
        .eq("id", input.document_id)
        .select(SELECT_COLUMNS)
        .maybeSingle();

      if (error) {
        console.error("[LegalRepository.upsert] update", error.message);
        return null;
      }
      if (!data) return null;
      return mapRow(data);
    }

    const { data, error } = await supabase
      .from("legal_documents")
      .upsert(row, { onConflict: "slug,locale,version" })
      .select(SELECT_COLUMNS)
      .single();

    if (error) {
      console.error("[LegalRepository.upsert] insert", error.message);
      return null;
    }

    return mapRow(data);
  }

  async getDonateLink(): Promise<string> {
    const supabase = await createClient();
    const { data, error } = await supabase
      .from("app_settings")
      .select("donate_link")
      .eq("id", "default")
      .maybeSingle();

    if (error) {
      console.error("[LegalRepository.getDonateLink]", error.message);
      return "";
    }

    return data?.donate_link ?? "";
  }

  async updateDonateLink(url: string): Promise<string | null> {
    const supabase = await createClient();
    const { data, error } = await supabase
      .from("app_settings")
      .upsert(
        { id: "default", donate_link: url },
        { onConflict: "id" },
      )
      .select("donate_link")
      .single();

    if (error) {
      console.error("[LegalRepository.updateDonateLink]", error.message);
      return null;
    }

    return data.donate_link;
  }
}
